import { camelCase } from "lodash";
import { ScoreStrength } from "@/components/controls/ScoreBars";
import { enumToString } from "@/utils/strings";
import { IcpFit, IcpFitSubject } from "./types";
import { camelizeFieldKey } from "@/utils/fieldKeys";
import { APIFieldItem, SettingsFieldType } from "@/pages/settings/types";

// UNKNOWN maps to the ScoreBars "unknown" strength, which draws three grey
// bars — scoring ran and returned no fit strength, which is a real answer and
// must still look like an indicator rather than bare text.
export const icpFitToScoreBarsMap: {
  [key: number]: ScoreStrength | undefined;
} = {
  [IcpFit.UNKNOWN]: "unknown",
  [IcpFit.NO_FIT]: "low",
  [IcpFit.POTENTIAL_FIT]: "medium",
  [IcpFit.GOOD_FIT]: "high"
};

export const getIcpFitUnknownTooltipTKey = (
  subject: IcpFitSubject = IcpFitSubject.GENERIC
): string => `icpFit.unknownTooltip.${subject}`;

export const getIcpFitTKey = (value: number | null | undefined): string => {
  const key = value != null ? enumToString(IcpFit, value as IcpFit) : "";
  return `icpFit.values.${camelCase(String(key || "unknown"))}`;
};

// Canonical registry field keys (camelCased) that hold an ICP fit value —
// the single source of truth for "which field is ICP fit". The direct
// people/companies field vs the two linked fields on leads are split because
// the feature-flag gating (excludeFieldsByKey) applies per entity type.
// The people/companies ICP fit column id used in ColumnDef, columnsState, and
// selectedColumns checks — a single constant prevents silent string drift.
export const ICP_FIT_COLUMN_ID = "icpFit";
export const ICP_FIT_PEOPLE_COMPANY_KEYS = [ICP_FIT_COLUMN_ID];
export const ICP_FIT_LEAD_KEYS = ["companyIcpFit", "contactIcpFit"];

export const isIcpFitFieldKey = (fieldKey: string): boolean =>
  [...ICP_FIT_PEOPLE_COMPANY_KEYS, ...ICP_FIT_LEAD_KEYS].includes(
    camelizeFieldKey(fieldKey)
  );

// The ICP Details blob on people/companies and its two linked fields on leads.
const ICP_METADATA_KEYS = [
  "icpMetadata",
  "companyIcpMetadata",
  "contactIcpMetadata"
];

// Custom and formula keys are derived from their label, so a user's own
// "ICP Fit" field can share an ICP key; only system and linked fields are ICP.
const ICP_FIELD_TYPES: ReadonlySet<SettingsFieldType> = new Set([
  SettingsFieldType.SYSTEM,
  SettingsFieldType.LINKED
]);

// ICP is surfaced only through its KPI, table column and Details popover, so
// its registry fields are never managed in Field Management nor listed as
// Details rows, whatever an account's stored show_in_detailed_view says.
// Covers fit and metadata; isIcpFitFieldKey above is the fit-only key check
// the tables use.
export const isIcpRegistryField = (
  field: Pick<APIFieldItem, "fieldKey" | "fieldType">
): boolean =>
  ICP_FIELD_TYPES.has(field.fieldType) &&
  (isIcpFitFieldKey(field.fieldKey) ||
    ICP_METADATA_KEYS.includes(camelizeFieldKey(field.fieldKey)));
